/**
 * DNDToggle Component
 * Do Not Disturb toggle with duration presets
 */

import { useState } from 'react';
import { useDND } from '../../hooks/useDND';

const DURATION_PRESETS = [
  { label: '15 минути', value: 15 },
  { label: '30 минути', value: 30 },
  { label: '1 час', value: 60 },
  { label: '2 часа', value: 120 },
  { label: 'Безкрайно', value: undefined },
] as const;

export const DNDToggle = () => {
  const { status, isLoading, enable, disable, isEnabling, isDisabling } = useDND();
  const [showDurationPicker, setShowDurationPicker] = useState(false);

  const handleToggle = () => {
    if (status?.enabled) {
      disable();
      setShowDurationPicker(false);
    } else {
      setShowDurationPicker(true);
    }
  };

  const handleDurationSelect = (duration?: number) => {
    enable(duration);
    setShowDurationPicker(false);
  };

  const getTimeRemaining = (): string => {
    if (!status?.enabled || !status?.mutedUntil) return '';

    if (status.isIndefinite) {
      return 'безкрайно';
    }

    const now = new Date();
    const until = new Date(status.mutedUntil);
    const diffMs = until.getTime() - now.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 60) {
      return `${diffMinutes} мин`;
    }

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (minutes === 0) {
      return `${hours} ч`;
    }

    return `${hours}ч ${minutes}мин`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Не безпокойте
          </h3>
          {status?.enabled && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Активен {status.isIndefinite ? 'безкрайно' : `за ${getTimeRemaining()}`}
            </p>
          )}
        </div>

        <button
          onClick={handleToggle}
          disabled={isLoading || isEnabling || isDisabling}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            ${status?.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
            ${(isLoading || isEnabling || isDisabling) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
              ${status?.enabled ? 'translate-x-6' : 'translate-x-1'}
            `}
          />
        </button>
      </div>

      {showDurationPicker && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            За колко време?
          </p>
          <div className="grid grid-cols-2 gap-2">
            {DURATION_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handleDurationSelect(preset.value)}
                disabled={isEnabling}
                className={`
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-white dark:bg-gray-700
                  border border-gray-300 dark:border-gray-600
                  text-gray-700 dark:text-gray-300
                  hover:bg-gray-50 dark:hover:bg-gray-600
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  transition-colors
                  ${isEnabling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowDurationPicker(false)}
            className="w-full mt-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Отказ
          </button>
        </div>
      )}
    </div>
  );
};
